import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { BILLING_API_BASE_URL, IAM_TENANT_SLUG } from "../config";

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export async function billingRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const session = loadStoredSession();
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  headers.set("X-Tenant-Slug", session?.tenant.slug ?? IAM_TENANT_SLUG);
  // Ne pas envoyer X-Tenant-ID IAM — UUID différent du tenant billing (même slug demo).

  if (session?.tokens.accessToken) {
    headers.set("Authorization", `Bearer ${session.tokens.accessToken}`);
  }

  const res = await fetch(`${BILLING_API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const body = (await res.json()) as ApiEnvelope<T>;
  if (!res.ok || !body.success) {
    if (res.status === 401) {
      throw new Error("Session expirée — reconnectez-vous depuis la Suite ITaaS");
    }
    if (res.status === 403) {
      throw new Error(
        body.message ??
          "Permissions billing manquantes — déconnectez-vous puis reconnectez-vous (Suite → Billing)",
      );
    }
    throw new Error(body.message ?? `Erreur API billing (${res.status})`);
  }

  return body.data;
}

export async function billingDownload(
  path: string,
  filenameFallback = "download.pdf",
): Promise<{ blob: Blob; filename: string }> {
  const session = loadStoredSession();
  const headers = new Headers();
  headers.set("X-Tenant-Slug", session?.tenant.slug ?? IAM_TENANT_SLUG);
  if (session?.tokens.accessToken) {
    headers.set("Authorization", `Bearer ${session.tokens.accessToken}`);
  }

  const res = await fetch(`${BILLING_API_BASE_URL}${path}`, { headers });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Session expirée — reconnectez-vous depuis la Suite ITaaS");
    }
    throw new Error(`Erreur téléchargement (${res.status})`);
  }

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? filenameFallback;
  const blob = await res.blob();
  return { blob, filename };
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function billingDownloadFile(path: string, filenameFallback?: string) {
  const { blob, filename } = await billingDownload(path, filenameFallback);
  triggerBrowserDownload(blob, filename);
}
